import React, {useEffect} from "react";
import { Grid, Button, Box, CircularProgress } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as yup from "yup";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { differenceInSeconds, format, parse, isValid, isAfter, addDays } from "date-fns";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import FormikTextInput from "@/components/forms/FormikTextInput";
import FormikLabel from "@/components/forms/FormikLabel";
import { useFetchSettingsRowsQuery } from "@/api/services/Settings/business/Business";
import { usePostShiftMasterMutation, useUpdateShiftMasterMutation } from "@/api/services/attendanceMaster/ShiftMaster";
import DialogForm from "@/components/modal/DialogForm";
import { toast } from "@/components/toastify";
import { StyledButton } from "@/components";

const validationSchema = yup.object().shape({
  shiftCode: yup.string().required("Shift Code is required"),
  shiftName: yup.string().required("Shift Name is required"),
  graceTime: yup
    .date()
    .nullable()
    .required("Grace Time is required"),
});

const ShiftForm = ({ handleClose, setOpen, open, selectedRow, setSelectedRow }) => {
  const [postShiftMaster] = usePostShiftMasterMutation();
  const [updateShiftMaster] = useUpdateShiftMasterMutation();
  const { data: shift = [] } = useFetchSettingsRowsQuery("Shift");
  const shiftTypeoptions = shift?.data || [];

  const calculateDuration = (start, end, setFieldValue) => {
    if (!start || !end) return;

    let adjustedEnd = end;

    // If end time is before or equal to start time, assume it's the next day
    if (!isAfter(end, start)) {
      adjustedEnd = addDays(end, 1);
    }

    const durationInSeconds = differenceInSeconds(adjustedEnd, start);

    const fullHours = Math.floor(durationInSeconds / 3600);
    const fullMinutes = Math.floor((durationInSeconds % 3600) / 60);
    const halfSeconds = durationInSeconds / 2;
    const halfHours = Math.floor(halfSeconds / 3600);
    const halfMinutes = Math.floor((halfSeconds % 3600) / 60);

    const fullDay = `${String(fullHours).padStart(2, "0")}:${String(fullMinutes).padStart(2, "0")}`;
    const halfDay = `${String(halfHours).padStart(2, "0")}:${String(halfMinutes).padStart(2, "0")}`;

    setFieldValue("fullDayTime", fullDay);
    setFieldValue("halfDayTime", halfDay);
  };



  const handleCloseDialog = () => {
    setSelectedRow(null);
    setOpen(false);
  };

  const handleSubmit = async (values, { resetForm }) => {
    const formData = {
      formName: "Shift Master",
      formFields: {
        shiftCode: values.shiftCode,
        shiftName: values.shiftName,
        graceTime: values.graceTime ? format(values.graceTime, "HH:mm") : "",
        inTime: values.shiftInTimeStart ? format(values.shiftInTimeStart, "HH:mm") : "",
        outTime: values.shiftInTimeEnd ? format(values.shiftInTimeEnd, "HH:mm") : "",
        halfDayTime: values.halfDayTime,
        fullDayTime: values.fullDayTime,
      },
    };
    try {
      if (selectedRow) {
        await updateShiftMaster({ formData, id: selectedRow.id }).unwrap();
        toast("success", "Shift updated successfully");
      } else {
        await postShiftMaster({ formData }).unwrap();
        toast("success", "Shift added successfully");
      }
      setOpen(false);
      setSelectedRow(null);
      resetForm();
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <DialogForm
      title={selectedRow ? "Update Shift" : "Add Shift"}
      open={open}
      setOpen={setOpen}
      width="md"
    >
      <Box padding={2}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Formik
            enableReinitialize
            initialValues={{
              shiftCode: selectedRow?.shiftCode || "",
              shiftName: selectedRow?.shiftName || "",
              graceTime: selectedRow?.graceTime ? parse(selectedRow.graceTime, "HH:mm", new Date()) : null,
              shiftInTimeStart: selectedRow?.inTime ? parse(selectedRow.inTime, "HH:mm", new Date()) : null,
              shiftInTimeEnd: selectedRow?.outTime ? parse(selectedRow.outTime, "HH:mm", new Date()) : null,
              halfDayTime: selectedRow?.halfDayTime || "",
              fullDayTime: selectedRow?.fullDayTime || "",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, resetForm, isSubmitting }) => {
              useEffect(() => {
                if (!open) {
                  resetForm();
                }
              }, [open, resetForm]);
              return (
                <Form>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <FormikTextInput
                        name="shiftCode"
                        label="Shift Code"
                        placeholder="Enter a shift code"
                        inputRestriction="alphanumeric"
                        required
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <FormikSelectInput
                        name="shiftName"
                        label="Shift Name"
                        placeholder="Select a shift name"
                        options={shiftTypeoptions.map((shift) => ({
                          value: shift.value,
                          label: shift.name,
                        }))}
                        required
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <FormikLabel text="Grace Time" required/>
                      <Field name="graceTime" >
                        {({ field, form }) => (
                          <>
                            <TimePicker
                              ampm={false}
                              value={field.value}
                              onChange={(newTime) => {
                                setFieldValue("graceTime", newTime);
                              }}
                              sx={{ width: "100%", ".MuiInputBase-root": { height: 40 } }}
                            />
                            {form.touched.graceTime && form.errors.graceTime && (
                              <div style={{ color: "red", fontSize: "12px" }}>
                                {form.errors.graceTime}
                              </div>
                            )}
                          </>
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <FormikLabel text="Shift In Time (Start)" />
                      <Field name="shiftInTimeStart">
                        {({ field }) => (
                          <TimePicker
                            ampm={false}
                            value={field.value}
                            onChange={(newTime) => {
                              setFieldValue("shiftInTimeStart", newTime);
                              if (values.shiftInTimeEnd) {
                                calculateDuration(newTime, values.shiftInTimeEnd, setFieldValue);
                              }
                            }}
                            sx={{ width: "100%", ".MuiInputBase-root": { height: 40 } }}
                          />
                        )}

                      </Field>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <FormikLabel text="Shift In Time (End)" />
                      <Field name="shiftInTimeEnd">
                        {({ field }) => (
                          <TimePicker
                            ampm={false}
                            value={field.value}
                            onChange={(newTime) => {
                              setFieldValue("shiftInTimeEnd", newTime);
                              if (values.shiftInTimeStart) {
                                calculateDuration(values.shiftInTimeStart, newTime, setFieldValue);
                              }
                            }}
                            sx={{ width: "100%", ".MuiInputBase-root": { height: 40 } }}
                          />
                        )}
                      </Field>
                    </Grid>

                    <Grid item xs={12} sm={2}>
                      <FormikTextInput
                        name="fullDayTime"
                        label="Full Day Time"
                        fullWidth
                        InputProps={{ readOnly: true }}
                        disabled
                      />
                    </Grid>

                    <Grid item xs={12} sm={2}>
                      <FormikTextInput
                        name="halfDayTime"
                        label="Half Day Time"
                        fullWidth
                        InputProps={{ readOnly: true }}
                        disabled
                      />
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                    <Grid item>
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        sx={{
                          width: "100px",
                          height: "40px",
                        }}
                        onClick={() => {
                          handleCloseDialog();
                          resetForm();
                        }}
                      >
                        Cancel
                      </Button>
                    </Grid>
                    <Grid item>

                      <StyledButton
                        type="submit"
                        label={
                          isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : selectedRow ? (
                            "Update"
                          ) : (
                            "Add"
                          )
                        }
                        variant="contained"
                      />
                    </Grid>
                  </Grid>
                </Form>
              )
            }}
          </Formik>
        </LocalizationProvider>
      </Box>
    </DialogForm>
  );
};

export default ShiftForm;
